import { createLogger } from "../../../../utils/logger";
import { getPublisherId } from "../auth/authClient";

export const listLogger = createLogger("CurationList");
const DEFAULT_SMART_LIST_LABEL = "action";
export const DEFAULT_SMART_LIST_TEMPLATE_ID = 107;
export const IMAGE_SERVICE_ADMIN_BASE_URL =
    "https://sales.image-service.eu-north-1-prod.vmnd.tv/admin";

/**
 * Ensure non-empty string
 * @param value
 * @returns {string | undefined}
 */
export const ensureTrimmed = (value: string | null | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Read response text and throw error if not ok
 * @param response
 * @param context
 * @param formatError - Optional callback to format custom error messages based on status and response text
 * @returns {Promise<string>}
 */
export const readTextOrThrow = async (
    response: Response,
    context: string,
    formatError?: (status: number, responseText: string) => string,
): Promise<string> => {
    const responseText = await response.text();
    if (!response.ok) {
        const errorMessage =
            formatError?.(response.status, responseText) ??
            `${context}: ${response.status} - ${responseText}`;
        throw new Error(errorMessage);
    }
    return responseText;
};

/**
 * Parse JSON or throw error
 * @param payload
 * @param context
 * @returns
 */
export const parseJsonOrThrow = <T>(payload: string, context: string): T => {
    try {
        return JSON.parse(payload) as T;
    } catch (error) {
        listLogger.error(`${context}: Failed to parse JSON`, error);
        throw new Error(`${context}: The API response was not valid JSON.`);
    }
};

/**
 * Resolve smart list labels
 * @param smartListLabels
 * @param smartListGenre
 * @returns {string}
 */
export const resolveSmartListLabels = (
    smartListLabels?: string[],
    smartListGenre?: string,
): string => {
    const normalizedLabels = smartListLabels
        ?.map((label) => label.trim().toLowerCase())
        .filter((label) => label.length > 0);

    if (normalizedLabels && normalizedLabels.length > 0) {
        return normalizedLabels.join(",");
    }
    const normalizedGenre = smartListGenre?.trim();
    if (normalizedGenre && normalizedGenre.length > 0) {
        return normalizedGenre;
    }
    return DEFAULT_SMART_LIST_LABEL;
};

/**
 * Resolve publisher ID from cookies or provided value
 * @param provided
 * returns {string}
 */
export const resolvePublisherId = (provided?: string): string => {
    const normalized = ensureTrimmed(provided) ?? ensureTrimmed(getPublisherId()) ?? "";

    if (!normalized) {
        throw new Error(
            "Publisher ID is missing from authentication cookies. Please log in again.",
        );
    }

    return normalized;
};
